import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import f1Service from '../../../components/dashboards/services/f1Service';

/**
 * F1 Live Race Tracker
 * Real-time race data widget for ongoing races
 * Shows live positions, gaps, and race progress
 */
const F1LiveRaceTracker = ({ season = 2025 }) => {
  const [liveRace, setLiveRace] = useState(null);
  const [raceResults, setRaceResults] = useState([]);
  const [startingGrid, setStartingGrid] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
 
  // Fetch live race data
  const fetchLiveRace = async () => {
    try {
      const response = await f1Service.getLiveRaces(season);

      if (response && response.response && response.response.length > 0) {
        const race = response.response[0];
        setLiveRace(race);

        // Fetch live results if available
        if (race.id) {
          try {
            const resultsResponse = await f1Service.getRaceResults(race.id);
            console.log('🏎️ Live Race Results:', resultsResponse?.response?.[0]); // Debug log
            if (resultsResponse && resultsResponse.response && resultsResponse.response.length > 0) {
              // Sort by position
              const sorted = resultsResponse.response.sort((a, b) => {
                return (a.position || 999) - (b.position || 999);
              });
              setRaceResults(sorted);
              setStartingGrid([]); // Clear starting grid if we have live results
            } else {
              // No live results yet, try to get starting grid
              try {
                const gridResponse = await f1Service.getStartingGrid(race.id);
                if (gridResponse && gridResponse.response && gridResponse.response.length > 0) {
                  const sortedGrid = gridResponse.response.sort((a, b) => {
                    return (a.position || 999) - (b.position || 999);
                  });
                  setStartingGrid(sortedGrid);
                  setRaceResults([]); // Clear results if we only have grid
                }
              } catch (gridErr) {
                console.log('No starting grid available');
              }
            }
          } catch (err) {
            console.log('No live results available yet, trying starting grid');
            // Try to get starting grid as fallback
            try {
              const gridResponse = await f1Service.getStartingGrid(race.id);
              if (gridResponse && gridResponse.response && gridResponse.response.length > 0) {
                const sortedGrid = gridResponse.response.sort((a, b) => {
                  return (a.position || 999) - (b.position || 999);
                });
                setStartingGrid(sortedGrid);
              }
            } catch (gridErr) {
              console.log('No starting grid available');
            }
          }
        }

        setLastUpdate(new Date());
        setError(null);
      } else {
        setLiveRace(null);
        setRaceResults([]);
      }
    } catch (err) {
      console.error('Error fetching live race:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchLiveRace();
  }, [season]);

  // Auto-refresh every 30 seconds when race is live
  useEffect(() => {
    if (!liveRace) return;

    const interval = setInterval(() => {
      fetchLiveRace();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [liveRace, season]);

  // Get team color
  const getTeamColor = (teamName) => {
    const colors = f1Service.getTeamColors(teamName);
    return colors.primary;
  };

  // Format time ago
  const formatTimeAgo = (date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <div className="h-4 w-32 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="p-8 text-center">
          <div className="inline-block w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          <p className="text-xs text-gray-600 font-mono uppercase tracking-wider mt-4">
            Checking for Live Races...
          </p>
        </div>
      </div>
    );
  }

  // No live race state
  if (!liveRace) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
            Live Race Tracker
          </h3>
        </div>
        <div className="p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 border-2 border-white/10 rounded-full flex items-center justify-center">
            <div className="w-6 h-6 bg-gray-700 rounded-full" />
          </div>
          <p className="text-xs text-gray-600 font-mono uppercase tracking-wider">
            No Live Race Currently
          </p>
          <p className="text-[10px] text-gray-700 font-mono mt-2">
            Check back when a race is in progress
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white/[0.02] border border-red-500/20 rounded-sm p-4">
        <p className="text-xs text-red-400 font-mono">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
      {/* Header with Live Indicator */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
              Live Race
            </h3>
            {/* Pulsing Live Indicator */}
            <div className="flex items-center gap-2">
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [1, 0.7, 1]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
                className="w-2 h-2 bg-red-500 rounded-full"
              />
              <span className="text-[10px] text-red-500 font-bold uppercase tracking-wider">
                Live
              </span>
            </div>
          </div>
          {lastUpdate && (
            <span className="text-[10px] text-gray-600 font-mono">
              Updated {formatTimeAgo(lastUpdate)}
            </span>
          )}
        </div>
      </div>

      {/* Race Info */}
      <div className="p-4 border-b border-white/5 bg-red-500/5">
        <div className="space-y-1">
          <div className="text-sm font-bold text-white">
            {liveRace.competition?.name || 'Grand Prix'}
          </div>
          <div className="text-xs text-gray-400 font-mono">
            {liveRace.circuit?.name || 'Circuit'}
          </div>
          <div className="flex items-center gap-4 text-[10px] text-gray-600 font-mono mt-2">
            {liveRace.competition?.location?.country && (
              <div>
                <span className="uppercase tracking-wider">
                  {liveRace.competition.location.country}
                </span>
              </div>
            )}
            {liveRace.laps && (
              <div className="flex items-center gap-1">
                <span className="text-white font-bold">{liveRace.laps.current || 0}</span>
                <span>/</span>
                <span>{liveRace.laps.total || '?'}</span>
                <span className="ml-1 uppercase tracking-wider">Laps</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Live Positions or Starting Grid */}
      {(raceResults.length > 0 || startingGrid.length > 0) ? (
        <>
          {/* Data Type Indicator */}
          {startingGrid.length > 0 && raceResults.length === 0 && (
            <div className="p-3 border-b border-white/5 bg-amber-500/5">
              <div className="text-[10px] text-amber-500 font-mono uppercase tracking-wider text-center">
                ⚠ Showing Starting Grid - Race has not started yet or live timing unavailable
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              {/* Table Header */}
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left p-3 text-gray-600 font-bold uppercase tracking-wider">P</th>
                  <th className="text-left p-3 text-gray-600 font-bold uppercase tracking-wider">Driver</th>
                  <th className="text-left p-3 text-gray-600 font-bold uppercase tracking-wider">Team</th>
                  <th className="text-right p-3 text-gray-600 font-bold uppercase tracking-wider">{raceResults.length > 0 ? 'Time/Gap' : 'Grid'}</th>
                  <th className="text-center p-3 text-gray-600 font-bold uppercase tracking-wider">{raceResults.length > 0 ? 'Pits' : 'Time'}</th>
                  <th className="text-right p-3 text-gray-600 font-bold uppercase tracking-wider">Laps</th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody>
                {(raceResults.length > 0 ? raceResults : startingGrid).map((result, index) => {
                const teamColor = getTeamColor(result.team?.name);
                const isPodium = result.position <= 3;
                const isLeader = result.position === 1;

                return (
                  <motion.tr
                    key={result.driver?.id || index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02, duration: 0.2 }}
                    className={`border-b border-white/5 transition-colors duration-150 ${
                      isLeader ? 'bg-red-500/10' : 'hover:bg-white/[0.02]'
                    }`}
                  >
                    {/* Position */}
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-bold tabular-nums ${
                            isPodium ? 'text-white' : 'text-gray-500'
                          }`}
                        >
                          {result.position || '—'}
                        </span>
                        {isPodium && (
                          <div
                            className="w-1 h-4 rounded-full"
                            style={{
                              backgroundColor:
                                result.position === 1
                                  ? '#FFD700'
                                  : result.position === 2
                                  ? '#C0C0C0'
                                  : '#CD7F32'
                            }}
                          />
                        )}
                      </div>
                    </td>

                    {/* Driver */}
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-0.5 h-5 rounded-full"
                          style={{ backgroundColor: teamColor }}
                        />
                        <div className="text-white font-semibold">
                          {result.driver?.abbr || result.driver?.name}
                        </div>
                      </div>
                    </td>

                    {/* Team */}
                    <td className="p-3">
                      <div
                        className="text-gray-400 truncate max-w-[120px]"
                        style={{ color: `${teamColor}cc` }}
                      >
                        {f1Service.getTeamCode(result.team?.name)}
                      </div>
                    </td>

                    {/* Time/Gap or Grid */}
                    <td className="p-3 text-right">
                      {raceResults.length > 0 ? (
                        // Live race - show time/gap
                        result.time ? (
                          <span className={`tabular-nums text-[11px] ${
                            isLeader ? 'text-white font-bold' : 'text-gray-400'
                          }`}>
                            {result.time}
                          </span>
                        ) : isLeader ? (
                          <span className="text-red-500 text-[10px] font-bold uppercase">
                            Leader
                          </span>
                        ) : (
                          <span className="text-gray-700 text-[10px]">—</span>
                        )
                      ) : (
                        // Pre-race - show grid position
                        <span className="text-gray-400 tabular-nums text-[11px]">
                          P{result.grid || result.position || '—'}
                        </span>
                      )}
                    </td>

                    {/* Pit Stops or Qualifying Time */}
                    <td className="p-3 text-center">
                      {raceResults.length > 0 ? (
                        // Live race - show pit stops
                        result.pits !== undefined && result.pits !== null ? (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-amber-500 text-[10px]">●</span>
                            <span className="text-gray-400 tabular-nums text-[11px]">
                              {result.pits}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-700 text-[10px]">—</span>
                        )
                      ) : (
                        // Pre-race - show qualifying time
                        <span className="text-gray-400 tabular-nums text-[11px]">
                          {result.time || '—'}
                        </span>
                      )}
                    </td>

                    {/* Laps Completed */}
                    <td className="p-3 text-right">
                      <span className="text-gray-400 tabular-nums text-[11px]">
                        {result.laps || liveRace.laps?.current || '—'}
                      </span>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      ) : (
        <div className="p-8 text-center space-y-3">
          <div className="inline-block w-8 h-8 border-2 border-white/10 border-t-amber-500 rounded-full animate-spin" />
          <p className="text-xs text-gray-600 font-mono uppercase tracking-wider">
            Race Detected - Loading Data...
          </p>
          <p className="text-[10px] text-gray-700 font-mono">
            {liveRace?.date && `Race Time: ${new Date(liveRace.date).toLocaleString()}`}
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="p-3 border-t border-white/5 bg-white/[0.01]">
        <div className="flex items-center justify-between text-[10px] text-gray-600 font-mono uppercase tracking-wider">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
            <span>Auto-Refresh: 30s</span>
          </div>
          {raceResults.length > 0 && (
            <span className="text-white font-bold">{raceResults.length} Drivers</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default F1LiveRaceTracker;
